import React, { useState, useEffect } from 'react'; // eslint-disable-line no-unused-vars
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import {
  Calendar, Users, CheckSquare, Sparkles,
  TrendingUp, X, ArrowRight, Command
} from 'lucide-react';

const quickActions = [
  {
    id: 'meeting',
    icon: Calendar,
    label: 'Log Meeting',
    description: 'Record a new client meeting',
    path: '/meetings',
    color: 'from-blue-500/20 to-blue-500/5',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600'
  },
  {
    id: 'client',
    icon: Users,
    label: 'Add Client',
    description: 'Create a new client profile',
    path: '/pipeline?action=add',
    color: 'from-emerald-500/20 to-emerald-500/5',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600'
  },
  {
    id: 'action',
    icon: CheckSquare,
    label: 'Action Items',
    description: 'View pending tasks',
    path: '/action-items',
    color: 'from-amber-500/20 to-amber-500/5',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600'
  },
  {
    id: 'pipeline',
    icon: TrendingUp,
    label: 'Pipeline',
    description: 'Manage your sales pipeline',
    path: '/pipeline',
    color: 'from-teal-500/20 to-teal-500/5',
    iconBg: 'bg-teal-50',
    iconColor: 'text-teal-600'
  },
];

export default function QuickActionPanel({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [hoveredAction, setHoveredAction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter actions based on search
  const filteredActions = quickActions.filter(action =>
    action.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    action.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle action click
  const handleActionClick = (action) => {
    navigate(action.path);
    onClose();
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className={cn(
          "relative w-full max-w-lg mx-4",
          "bg-white/80 backdrop-blur-2xl border border-white/60 rounded-2xl",
          "shadow-2xl shadow-black/10",
          "animate-in fade-in slide-in-from-top-4 duration-200"
        )}
      >
        {/* Search Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-black/5">
          <Sparkles className="w-5 h-5 text-accent" />
          <input
            type="text"
            placeholder="What would you like to do?"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground/60 focus:outline-none text-sm"
            autoFocus
          />
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-black/5 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Actions List */}
        <div className="p-3 space-y-1">
          {filteredActions.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No actions found for "{searchQuery}"
            </div>
          ) : (
            filteredActions.map((action) => (
              <button
                key={action.id}
                onClick={() => handleActionClick(action)}
                onMouseEnter={() => setHoveredAction(action.id)}
                onMouseLeave={() => setHoveredAction(null)}
                className={cn(
                  "w-full flex items-center gap-4 px-4 py-3 rounded-xl",
                  "transition-all duration-150 text-left",
                  "border border-transparent",
                  hoveredAction === action.id
                    ? `bg-gradient-to-r ${action.color} border-accent/20 shadow-md shadow-accent/10`
                    : "hover:bg-black/[0.02]"
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
                  action.iconBg
                )}>
                  <action.icon className={cn("w-5 h-5", action.iconColor)} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground">
                    {action.label}
                  </div>
                  <div className="text-xs text-muted-foreground/70 truncate">
                    {action.description}
                  </div>
                </div>

                <ArrowRight className={cn(
                  "w-4 h-4 text-muted-foreground/50 transition-all duration-150",
                  hoveredAction === action.id && "text-accent translate-x-0.5"
                )} />
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-black/5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground/60">
            <span>Press</span>
            <kbd className="px-2 py-1 bg-black/5 rounded text-[10px] font-mono font-medium">
              ESC
            </kbd>
            <span>to close</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
            <Command className="w-3 h-3" />
            <span>+ K to toggle</span>
          </div>
        </div>
      </div>
    </div>
  );
}
